import { SavedPrompt, SavedPromptInstance } from "./OpsGenieAppSetting.types";
import { DateTime } from "luxon";
import { IOpsGenieContext } from "./Components/OpsGenieBody.types";
import { OpsGenieHypothesisState } from "./OpsGenieHypothesisState";
import { SuggestPrompt } from "Core/oRPC";
import { TFormSchema } from "./Components/OpsGenieChat/ChatBox/WorkflowForms/PoisonBuildForm.types";
import { TeamAppModel } from "Apps/TeamAppModel";
import { OPSGENIE_V5_COPILOT_TYPE } from "./Utils/OpsGenieAgent.constants";
import { newGuid } from "Shared/Utils";
import { types } from "mobx-state-tree";
export const OpsGenieAppSetting = types.compose(
    "opsGenieAppSetting",
    OpsGenieHypothesisState,
    types
        .model({
            conversationId: types.optional(types.string, newGuid()),
            conversationContext: types.optional(types.frozen(types.map(types.string)), {}),
            copilotType: types.optional(types.string, OPSGENIE_V5_COPILOT_TYPE),
            selectedModel: types.optional(types.string, ""),
            isRightPanel: types.optional(types.boolean, false),
            activityId: types.optional(types.string, ""),
            lastMessageId: types.optional(types.string, ""),
            clientMessage: types.optional(types.string, ""),
            isFetchingAnswer: types.optional(types.boolean, false),
            showingPopover: types.optional(types.boolean, true),
            contextTimestamp: types.optional(types.string, ""),
            savedPrompts: types.optional(types.array(SavedPrompt), []),
            isDebugMode: types.optional(types.boolean, false),
            isFullScreenView: types.optional(types.boolean, false),
            isVisualizationEnabled: types.optional(types.boolean, false),
            workflowForm: types.optional(types.frozen<TFormSchema | undefined>(), undefined),
            isWorkflowMode: types.optional(types.boolean, false)
        })
        .views((self) => ({
            get currentDebugMode() {
                return self.isDebugMode;
            },
            get currentFullScreenView() {
                return self.isFullScreenView;
            },
            get currentConversationId() {
                return self.conversationId;
            },
            get currentCopilotType() {
                return self.copilotType;
            },
            get currentSelectedModel() {
                return self.selectedModel;
            },
            get isRightPanelOpen() {
                return self.isRightPanel;
            },
            get currentConversationContext() {
                if (self.conversationContext?.AssistantName !== self.copilotType) {
                    return {
                        ...self.conversationContext,
                        AssistantName: self.copilotType
                    };
                }
                return self.conversationContext;
            },
            get currentContextTimestamp() {
                return self.contextTimestamp;
            },
            get currentActivityId() {
                return self.activityId;
            },
            get fetchingAnswer() {
                return self.isFetchingAnswer;
            },
            get lastMessage() {
                return self.lastMessageId;
            },
            get currentClientMessage() {
                return self.clientMessage;
            },
            get isShowingPopover() {
                return self.showingPopover;
            },
            get currentSavedPrompts() {
                return self.savedPrompts;
            },
            get groupedSavedPrompts() {
                const groupedPrompts = self.savedPrompts.reduce<Record<string, Array<SuggestPrompt>>>((acc, prompt) => {
                    const promptsCopilot = prompt.copilot;
                    if (!acc[promptsCopilot]) {
                        acc[promptsCopilot] = [];
                    }
                    acc[promptsCopilot].push(prompt);
                    return acc;
                }, {});
                return Object.entries(groupedPrompts);
            },
            get currentWorkflowForm(): TFormSchema | undefined {
                return self.workflowForm;
            },
            get requestId() {
                return self.workflowForm?.["Request id"] || "<requestId>";
            },
            get currentIsWorkflowMode() {
                return self.isWorkflowMode;
            }
        }))
        .actions((self) => ({
            setIsVisualizationEnabled(value: boolean) {
                self.isVisualizationEnabled = value;
            },
            setIsWorkflowMode(value: boolean) {
                self.isWorkflowMode = value;
            },
            setWorkflowForm(form: TFormSchema) {
                self.workflowForm = form;
            },
            clearWorkflowForm() {
                self.workflowForm = undefined;
            },
            setIsDebugMode(value: boolean) {
                self.isDebugMode = value;
            },
            setIsFullScreenView(value: boolean) {
                self.isFullScreenView = value;
            },
            setCurrentConversationId(newId: string) {
                self.conversationId = newId;
            },
            setCurrentCopilotType(newType: string) {
                self.copilotType = newType;
                self.conversationContext = {
                    ...self.conversationContext,
                    AssistantName: newType
                };
            },
            setSelectedModel(modelName: string) {
                self.selectedModel = modelName;
            },
            setIsRightPanelOpen(isOpen: boolean) {
                self.isRightPanel = isOpen;
            },
            setCurrentActivityId(newId: string) {
                self.activityId = newId;
            },
            setFetchingAnswer(isFetching: boolean) {
                self.isFetchingAnswer = isFetching;
            },
            setLastMessageId(newId: string) {
                self.lastMessageId = newId;
                self.contextTimestamp = DateTime.utc().toString();
            },
            setCurrentClientMessage(newQuestion: string) {
                self.clientMessage = newQuestion;
            },
            setCurrentConversationContext(newModel: IOpsGenieContext) {
                self.conversationContext = newModel;
                self.contextTimestamp = DateTime.utc().toString();
            },
            updateShowingPopover(isShowing: boolean) {
                self.showingPopover = isShowing;
            },
            updateAppKeepContext(app: TeamAppModel) {
                self.conversationId = self.conversationId || newGuid();
                self.conversationContext = {
                    TopicName: self.conversationContext?.TopicName || app.description,
                    AssistantName: !self.conversationContext?.AssistantName ? "OpsGenie" : self.conversationContext.AssistantName,
                    AppName: app.title,
                    IncidentId: self.conversationContext?.IncidentId || "",
                    ServiceCategoryId: self.conversationContext?.ServiceCategoryId || ""
                };
                self.contextTimestamp = DateTime.utc().toString();
            },
            updateIncidentId(incidentId: string) {
                self.conversationContext = {
                    ...self.conversationContext,
                    IncidentId: incidentId
                };
                self.workflowForm = {
                    ...(self.workflowForm || {}),
                    IncidentId: incidentId
                };
                self.contextTimestamp = DateTime.utc().toString();
            },
            updateDeploymentRing(deploymentRing: string) {
                self.conversationContext = {
                    ...self.conversationContext,
                    DeploymentRing: deploymentRing
                };
                self.contextTimestamp = DateTime.utc().toString();
                self.workflowForm = {
                    ...(self.workflowForm || {}),
                    DeployRings: deploymentRing.split(",")
                };
            },
            updateSavedPrompts(prompts: SavedPromptInstance) {
                const promptsToSave = self.savedPrompts.filter((value) => value.title !== prompts.title);
                self.savedPrompts.replace(promptsToSave.length === self.savedPrompts.length ? [...self.savedPrompts, prompts] : promptsToSave);
            }
        }))
);
